'use client';
/**
 * Équipements × postes de travail — lots N7-3 et N7-4.
 *
 * ── Pourquoi un fichier de composants et pas un écran de plus ──────────────
 *
 * Le rattachement ne vaut que par les deux questions qu'il permet de poser, et
 * ces deux questions ne se posent pas au même endroit :
 *
 *  · sur la fiche d'un POSTE — « une personne affectée ici, sur quoi doit-elle
 *    être habilitée ? » ;
 *  · sur la fiche d'un ÉQUIPEMENT — « qui peut faire tourner cet automate ? ».
 *
 * Un écran isolé « rattachements » n'aurait répondu ni à l'une ni à l'autre :
 * on ne l'ouvre que si l'on sait déjà qu'il existe. Les trois composants d'ici
 * vont donc SE POSER dans les écrans qui existent — composition des postes,
 * fiche d'équipement, formulaire d'ajout — et ils lisent tous la même route,
 * pour qu'aucun ne puisse dire autre chose que l'autre.
 *
 * ⚠️ Adresse réelle : `/api/ged/ged/…` — DOUBLE `ged`. Le proxy est monté sur
 * `/api/ged/` et le moteur porte aussi `ged`. Ce piège a rendu toute la famille
 * N7 injoignable une première fois.
 */
import React from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { I, card, inputStyle, EmptyState, ChampRecherche} from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { fmtDate } from '@/lib/meta';
import { Tableau, enTeteCollant } from '@/shared/Tableau';
import { useGed, userName } from '@/lib/store';
/* ------------------------------------------------------------------------ */
/* Ce que le moteur rend                                                     */
/* ------------------------------------------------------------------------ */

export type EquipementDuPoste = {
  code: string; nom: string;
  origine: 'pose' | 'habilitation';
  habilitation?: string;
  par?: string | null; le?: string | null; statut?: string | null;
  accessoires: { code: string; nom: string; statut?: string | null }[];
};
export type VuePoste = {
  code: string; libelle: string;
  equipements: EquipementDuPoste[];
  introuvables: string[]; accessoiresPoses: string[];
  nbPoses: number; nbParHabilitation: number; nbAccessoires: number;
};
export type Chiffrage = {
  equipements: number; mesure: number; accessoires: number;
  rattaches: number; rattachesPose: number; rattachesHabilitation: number;
  sansPoste: number; postes: number; postesAvecEquipement: number;
  postesSansEquipement: string[]; accessoiresSansAutomate: number;
  /** parmi les `sansPoste`, ceux qu'une décision signée a ÉCARTÉS de la reprise */
  sansPosteEcartes: number;
  /** ce qui RESTE à traiter : `sansPoste − sansPosteEcartes`. Deux chiffres et
   *  non un — une fiche écartée reste une fiche qu'aucun poste n'utilise, et
   *  l'auditeur a le droit de la compter ; mais la séance ne doit plus la
   *  redemander. */
  sansPosteATraiter: number;
};
/** Une fiche ÉCARTÉE de la reprise — la décision voyage avec elle. */
export type LigneEcartee = {
  equipement: string; nom: string;
  discipline?: string | null; localisation?: string | null;
  ecartement: { motif: string; par: string; le: string | null };
};
export type Proposition = {
  code: string; libelle: string; motif: string;
  confiance: 'sur' | 'probable'; signaux: string[];
};
export type ResultatProposition = {
  propositions: Proposition[];
  silence: string | null;
  redirection: { automate: string | null; message: string } | null;
  ecartes: { code: string; libelle: string; pourquoi: string }[];
};
export type VueEquipement = {
  code: string;
  postes: { code: string; libelle: string; origine: 'pose' | 'habilitation'; habilitation?: string }[];
  sertAutomate?: string | null;
  posteHerites?: { code: string; libelle: string; via: string }[];
  note?: string;
};

const encadre = (fond: string, bord: string): React.CSSProperties => ({
  ...card, padding: 14, background: fond, border: `1px solid ${bord}`,
});

/** La pastille qui distingue les deux liens. Elle n'est pas décorative : c'est
 *  elle qui empêche de croire qu'un équipement « posé » exige une habilitation
 *  qui n'existe pas, et qu'un équipement « exigé » se détache d'un clic. */
export function OrigineLien({ e }: { e: { origine: 'pose' | 'habilitation'; habilitation?: string } }) {
  const exige = e.origine === 'habilitation';
  return (
    <span title={exige
      ? `Exigé par l’habilitation « ${e.habilitation} » : on ne peut pas tenir ce poste sans elle. Pour le retirer, retirez l’habilitation des exigences du poste.`
      : 'Posé à la main avant le 28/10/2026 : à convertir en habilitation ou à retirer à la reprise.'}
      style={{
        display: 'inline-block', fontSize: 12.5, fontWeight: 700, padding: '2px 7px',
        borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap',
        background: exige ? 'var(--blue-50)' : 'var(--gray-50)',
        color: exige ? 'var(--blue-700)' : 'var(--text-muted)',
        border: `1px solid ${exige ? 'var(--blue-300)' : 'var(--border-default)'}`,
      }}>
      {exige ? `exigé · ${e.habilitation}` : 'ancien rattachement à la main'}
    </span>
  );
}

/* ------------------------------------------------------------------------ */
/* 1 · Le panneau du POSTE — N7-3                                            */
/* ------------------------------------------------------------------------ */

/**
 * « Quels équipements utilise-t-on à ce poste ? », et le geste de le corriger.
 *
 * Le tableau distingue toujours les deux liens. Seuls les équipements POSÉS
 * portent un bouton « Retirer » : ceux qu'une habilitation exige ne se
 * détachent pas d'ici, et l'écran dit où aller pour cela plutôt que d'offrir un
 * bouton qui échouerait.
 */
export function PanneauEquipementsPoste({ posteCode, posteLibelle }: { posteCode: string; posteLibelle: string }) {
  const [vue, setVue] = React.useState<VuePoste | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [brouillon, setBrouillon] = React.useState<string[] | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  // Un COMPTEUR de rechargement, jamais un setState au corps d'un effet :
  // `react-hooks/set-state-in-effect` est une erreur de lint dans ce dépôt.
  const [tour, setTour] = React.useState(0);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes/equipements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant) return;
        const v = (j?.postes || []).find((p: VuePoste) => p.code === posteCode) || null;
        if (j) { setVue(v); setErreur(''); } else setErreur('Les équipements des postes n’ont pas pu être lus.');
      })
      .catch(() => { if (vivant) setErreur('Les équipements des postes n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [posteCode, tour]);

  if (erreur) {
    return <div style={encadre('var(--red-50)', 'var(--red-300)')}><strong style={{ fontSize: 12.5 }}>{erreur}</strong></div>;
  }
  if (!vue) return <div style={{ ...card, padding: 18 }}><p style={{ fontSize: 12.5, margin: 0 }}>Lecture…</p></div>;

  const poses = vue.equipements.filter((e) => e.origine === 'pose').map((e) => e.code);
  const choix = brouillon ?? poses;
  const modifie = brouillon !== null;

  const enregistrer = async () => {
    setEnCours(true);
    const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(posteCode)}/equipements`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      /* `base` (14/10/2026) : les appareils posés LUS. Le moteur n'applique
         que les ajouts et retraits de cet écran à la liste fraîche. */
      body: JSON.stringify({ equipements: choix.map((code) => ({ code })), base: poses }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Rattachement refusé.'); return; }
    setErreur('');
    setMessage('Ancien(s) rattachement(s) retiré(s).');
    setBrouillon(null);
    setTour((t) => t + 1);
  };

  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 4px' }}>
        Équipements de ce poste — {vue.equipements.length}
        {modifie && <span style={{ color: 'var(--amber-600)', fontWeight: 600, fontSize: 12.5 }}> · non enregistré</span>}
      </h2>
      {/* LE LIEN SE DÉDUIT (Ines, 28/10/2026) : un appareil sert à ce poste dès
          que le poste exige une habilitation qui le couvre. On ne l'ajoute plus
          ici ; on change les exigences du poste, ou les habilitations de
          l'appareil depuis sa fiche. */}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Les appareils couverts par les habilitations qu’exige « {posteLibelle} » — c’est la réponse à
        « une personne affectée ici, sur quoi doit-elle être habilitée ? ». Pour changer cette liste,
        modifiez les habilitations exigées par le poste, ou celles d’un appareil depuis sa fiche.
        {vue.nbPoses > 0 && <> <strong>{vue.nbPoses}</strong> ancien(s) rattachement(s) à la main restent à reprendre.</>}
      </p>

      {!!message && (
        <div style={{ ...encadre('var(--green-50)', 'var(--green-700)'), marginBottom: 12 }}>
          <strong style={{ fontSize: 12.5, color: 'var(--green-700)' }}>{message}</strong>
        </div>
      )}

      {/* L10 (11/10/2026) : sous-tableau SIMPLE sur le socle — « Retirer » en colonne
          d'actions, les accessoires repliés sous la désignation. */}
      <Tableau
        id="poste-equipements"
        ajusterLargeur
        niveau="simple"
        lignes={vue.equipements}
        cleLigne={(e) => e.code}
        vide={{ titre: 'Aucun équipement à ce poste', indice: 'Ni exigé par une habilitation, ni posé à la main. Ce n’est pas forcément un manque — un poste d’accueil n’utilise aucun automate.' }}
        colonnes={[
          /* Code et nom dans DEUX colonnes : « EQ03 — Erba Lyte Pro » d'un bloc ne
             s'alignait pas d'une ligne à l'autre. */
          { cle: 'code', libelle: 'Code', valeur: (e) => e.code, style: { width: 72, fontFamily: 'var(--font-mono)', fontWeight: 700, verticalAlign: 'top' }, rendu: (e) => e.code },
          {
            cle: 'nom', libelle: 'Équipement', valeur: (e) => e.nom, style: { lineHeight: 1.45 },
            rendu: (e) => (
              <>
                {e.nom}
                {e.statut === 'hors_service' && <span style={{ color: 'var(--red-600)', fontSize: 12.5, fontWeight: 700 }}> · hors service</span>}
                {!!e.accessoires.length && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, paddingInlineStart: 12, borderInlineStart: '2px solid var(--border-default)' }}>
                    {/* Les accessoires SUIVENT : ils ne se rattachent pas, mais une panne de clavier immobilise bien le poste. */}
                    {e.accessoires.map((a) => `${a.code} ${a.nom}`).join(' · ')}
                    <br />
                    <em>accessoire(s) servant cet appareil — ils suivent, ils ne se rattachent pas séparément</em>
                  </div>
                )}
              </>
            ),
          },
          { cle: 'origine', libelle: 'Origine', type: 'liste', valeur: (e) => e.origine, libelleValeur: (v) => (v === 'pose' ? 'posé à la main' : 'exigé par une habilitation'), style: { width: 210 }, rendu: (e) => <OrigineLien e={e} /> },
        ]}
        actions={(e) => (e.origine === 'pose' ? (
          <Bouton taille="sm" onClick={() => { setMessage(''); setBrouillon(choix.filter((c) => c !== e.code)); }}
            title="Ancien rattachement à la main : le retirer">
            <I n="x" s={12} /> Retirer
          </Bouton>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }} title="Déduit de l’habilitation exigée par le poste.">
            via l’habilitation
          </span>
        ))}
      />

      {!!vue.introuvables.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {vue.introuvables.length} code(s) rattaché(s) ne correspondent à aucune fiche du parc
          ({vue.introuvables.join(' · ')}). Rien n’a été effacé : un rattachement supprimé en silence ne
          se verrait pas passer.
        </p>
      )}
      {!!vue.accessoiresPoses.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {vue.accessoiresPoses.join(' · ')} : ce sont des ÉQUIPEMENTS ACCESSOIRES. Ils ne se rattachent pas
          à un poste — ils suivent l’automate qu’ils servent. À corriger depuis « Accessoires &amp; qualification ».
        </p>
      )}

      {modifie && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <Bouton taille="sm" onClick={() => { setBrouillon(null); setMessage(''); }}>Annuler</Bouton>
          <Bouton variante="primaire" disabled={enCours} onClick={() => void enregistrer()}>
            Enregistrer le retrait
          </Bouton>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 2 · La RÉCIPROQUE, sur la fiche de l'équipement                           */
/* ------------------------------------------------------------------------ */

/**
 * « Qui peut travailler sur cet automate ? » — la même relation, lue de
 * l'autre côté. C'est la question qu'un auditeur pose en circulant, et elle
 * n'avait aucune réponse à l'écran avant ce lot.
 *
 * Rendue par POSTE et non par personne : les personnes s'en déduisent par le
 * planning et la matrice, qui existent déjà — les recalculer ici les ferait
 * diverger.
 */
export function PostesDeLEquipement({ documentId, version = 0 }: { documentId: string; version?: number }) {
  const roles = useGed((s) => s.session.roles);
  const [vue, setVue] = React.useState<VueEquipement | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [tour, setTour] = React.useState(0);
  // Retirer un ANCIEN lien posé : les mêmes rôles que le moteur (`saveEquipements`).
  const peutRetirer = ['rq', 'rt', 'rh'].some((r) => roles.includes(r));

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/equip/${encodeURIComponent(documentId)}/postes`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) { setVue(j); setErreur(''); } else setErreur('Les postes de travail n’ont pas pu être lus.'); })
      .catch(() => { if (vivant) setErreur('Les postes de travail n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [documentId, tour, version]);

  if (erreur) return <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>;
  if (!vue) return <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Lecture…</p>;

  /* Retirer un lien posé AUTREFOIS à la main — le seul geste qui reste ici,
     le temps de la reprise. `base` ne nomme que cet appareil : le moteur ne
     retire que lui. */
  const retirer = async (poste: { code: string; libelle: string }) => {
    setEnCours(true); setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(poste.code)}/equipements`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipements: [], base: [vue.code] }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setMessage(d?.error?.message || 'Retrait refusé.'); return; }
    setMessage(`Ancien rattachement à « ${poste.libelle} » retiré.`);
    setTour((t) => t + 1);
  };

  const herites = vue.posteHerites || [];
  const deduits = vue.postes.filter((p) => p.origine === 'habilitation');
  const anciens = vue.postes.filter((p) => p.origine === 'pose');
  return (
    <div>
      {/* ⚠️ LE LIEN SE DÉDUIT, IL NE SE POSE PLUS (Ines, 28/10/2026) : « un
          équipement nécessite une habilitation et le poste nécessite une
          habilitation ». L'appareil sert à un poste dès que le poste exige une
          des habilitations qui le couvrent — on change l'un ou l'autre côté,
          jamais le lien lui-même. */}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
        Les postes qui exigent une des habilitations de cet appareil — donc <strong>où il sert</strong>, qui
        doit être habilité dessus et qui reçoit ses maintenances au planning. Pour changer cette liste,
        modifiez les habilitations de l’appareil (bloc ci-dessus) ou celles qu’exige le poste.
      </p>
      {!!vue.note && <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '0 0 8px' }}>{vue.note}</p>}

      {!deduits.length && !anciens.length && !herites.length ? (
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
          <strong>Aucun poste.</strong> Aucun poste n’exige d’habilitation couvrant cet appareil : rattachez-le à
          l’habilitation de son activité pour qu’il apparaisse sur les postes qui l’exigent.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
          {deduits.map((p) => (
            <li key={`${p.code}-h`}>
              <strong>{p.libelle}</strong> <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({p.code})</span>
              <span style={{ color: 'var(--text-muted)' }}> — exige {p.habilitation}</span>
            </li>
          ))}
          {anciens.map((p) => (
            <li key={`${p.code}-p`}>
              <strong>{p.libelle}</strong> <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({p.code})</span>
              {' '}<span title="Posé à la main avant le 28/10/2026. Il sera converti en habilitation ou retiré à la reprise."
                style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber-700)' }}>ancien rattachement à la main</span>
              {peutRetirer && !vue.sertAutomate && (
                <Bouton taille="sm" variante="discret" disabled={enCours} style={{ marginInlineStart: 6 }}
                  title={`Retirer l’ancien rattachement à « ${p.libelle} »`} onClick={() => void retirer(p)}>
                  <I n="x" s={12} /> Retirer
                </Bouton>
              )}
            </li>
          ))}
        </ul>
      )}
      {!!message && <p style={{ fontSize: 12.5, margin: '8px 0 0', fontWeight: 600 }}>{message}</p>}

      {!!vue.sertAutomate && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          Cet équipement est un <strong>accessoire</strong> de {vue.sertAutomate} : il ne se rattache pas à un
          poste de son côté. {herites.length
            ? <>Il immobilise en revanche {herites.map((p) => p.libelle).join(', ')} — c’est ce qu’une panne
              de cet accessoire arrête réellement.</>
            : <>L’automate qu’il sert n’est lui-même rattaché à aucun poste.</>}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 2 bis · Les HABILITATIONS de l'appareil, choisies à l'ajout (28/10/2026)   */
/* ------------------------------------------------------------------------ */

const sansAccent = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/**
 * Ce qu'il faut savoir pour se servir de l'appareil, dit DÈS L'AJOUT.
 *
 * « Un équipement nécessite une habilitation et le poste nécessite une
 * habilitation » (Ines, 28/10/2026) : on ne choisit plus de postes, on choisit
 * les habilitations — les postes qui les exigent recevront l'appareil d'eux-mêmes.
 *
 * Proposition PRÉ-COCHÉE et refusable, comme celle des postes avant elle
 * (`useHabilitationsProposees`) :
 *  · l'habilitation dont un appareil couvert a la MÊME discipline ;
 *  · ACT-SOCLE-TECHNIQUE pour un instrument de MÉTROLOGIE (décision du 28/10).
 * Dès qu'on touche une case, le formulaire garde SA sélection.
 */
export type HabilitationChoisissable = { code: string; libelle: string; actif: boolean; equipementCodes: string[] };

/** Les habilitations du référentiel, et celles qu'on PROPOSE pour un appareil de
 *  cette discipline / nature. Un hook, pour que le formulaire tienne lui-même
 *  la sélection : « proposée tant qu'on n'y a pas touché », sans remonter
 *  d'état depuis un rendu ni depuis un effet. */
export function useHabilitationsProposees(discipline?: string, nature?: string) {
  const equips = useGed((s) => s.equips);
  const [habs, setHabs] = React.useState<HabilitationChoisissable[] | null>(null);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/habilitations', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setHabs(((j?.habilitations || []) as (HabilitationChoisissable & { equipementCodes?: string[] })[])
        .filter((h) => h.actif !== false).map((h) => ({ ...h, equipementCodes: h.equipementCodes || [] }))); })
      .catch(() => { if (vivant) setHabs([]); });
    return () => { vivant = false; };
  }, []);
  const disciplineDe = React.useMemo(() => new Map(equips.filter((e) => e.code).map((e) => [String(e.code), sansAccent(String(e.discipline || ''))])), [equips]);
  const proposees = React.useMemo(() => {
    if (!habs || nature === 'accessoire') return [] as string[];
    if (nature === 'metrologie') return habs.some((h) => h.code === 'ACT-SOCLE-TECHNIQUE') ? ['ACT-SOCLE-TECHNIQUE'] : [];
    const d = sansAccent(discipline || '');
    if (!d) return [];
    return habs.filter((h) => h.equipementCodes.some((c) => disciplineDe.get(c) === d)).map((h) => h.code);
  }, [habs, discipline, nature, disciplineDe]);
  return { habs, proposees };
}

export function ChoixHabilitations({ habs, proposees, nature, choix, onChoix }: {
  habs: HabilitationChoisissable[] | null; proposees: string[]; nature?: string;
  choix: string[]; onChoix: (v: string[]) => void;
}) {
  if (!habs) return null;
  if (nature === 'accessoire') {
    return (
      <div style={encadre('var(--amber-50)', 'var(--amber-300)')}>
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          Un <strong>accessoire</strong> n’exige pas d’habilitation : il suit l’automate qu’il sert, et les postes de cet automate.
        </p>
      </div>
    );
  }
  const basculer = (code: string) => onChoix(choix.includes(code) ? choix.filter((c) => c !== code) : [...choix, code]);
  const tri = [...habs].sort((a, b) => Number(proposees.includes(b.code)) - Number(proposees.includes(a.code)) || a.libelle.localeCompare(b.libelle, 'fr'));
  return (
    <div style={encadre('var(--gray-25)', 'var(--border-default)')}>
      <strong style={{ fontSize: 12.5 }}>Habilitations qu’exige cet appareil</strong>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 10px', lineHeight: 1.5 }}>
        L’appareil servira à chaque poste qui exige l’une d’elles. Proposées d’après la discipline saisie
        {nature === 'metrologie' ? ' (instrument de métrologie : l’habilitation socle commune)' : ''} — tout se décoche.
        Réservé au RQ : sans ce rôle, la fiche est créée et les habilitations restent à poser.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 5 }}>
        {tri.map((h) => (
          <label key={h.code} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={choix.includes(h.code)} onChange={() => basculer(h.code)} />
            <span>{h.libelle}{proposees.includes(h.code) && <span style={{ color: 'var(--text-muted)' }}> · proposée</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 3 · La PROPOSITION à l'ajout — N7-4                                       */
/* ------------------------------------------------------------------------ */

/**
 * Ce que l'outil propose AU MOMENT où l'on ajoute un équipement.
 *
 * ── Les trois règles que ce composant tient ────────────────────────────────
 *
 *  1. la proposition est REFUSABLE. Chaque case se décoche, et un refus ne
 *     laisse aucune trace : le parent n'envoie que ce qui est coché ;
 *  2. elle est CORRIGEABLE. Tous les postes restent offerts, pas seulement les
 *     proposés — un pré-remplissage qu'on ne peut pas contredire produit des
 *     données fausses que personne ne relit ;
 *  3. elle DIT sur quoi elle repose. Une case cochée sans motif ne se relit
 *     pas, et c'est la relecture qui transforme une déduction en décision.
 *
 * ⚠️ Seules les propositions portées par la DISCIPLINE sont pré-cochées. La
 * validation croisée du 07/08 (on cache le rattachement réel d'un des 27
 * automates du parc, on redemande) donne 86 % de justesse et 100 % de rappel
 * ainsi ; en pré-cochant aussi le LIEU, la justesse tombe à 83 % et le rappel à
 * 80 % — « salle immunologie » abrite l'immunologie et l'hémostase. Le lieu
 * propose donc, mais ne coche pas.
 */
export function PropositionPostes({
  nom, categorie, nature, discipline, localisation, emplacementCode, choix, onChoix,
}: {
  nom: string; categorie?: string; nature?: string;
  discipline?: string; localisation?: string; emplacementCode?: string;
  choix: string[]; onChoix: (v: string[]) => void;
}) {
  const [res, setRes] = React.useState<ResultatProposition | null>(null);
  const [tousPostes, setTousPostes] = React.useState<{ code: string; libelle: string }[]>([]);
  const [ouvertTous, setOuvertTous] = React.useState(false);
  /* Tant que personne n'a coché ni décoché, la proposition PRÉ-COCHE. Dès qu'on
   * y touche, elle ne recoche plus rien : sans ce drapeau, décocher une case
   * serait impossible — elle se recocherait au recalcul suivant, et la
   * proposition cesserait d'être refusable. */
  const touche = React.useRef(false);
  // La signature des critères : la proposition se recalcule quand ils changent,
  // pas à chaque frappe dans un champ qui ne la concerne pas.
  const cle = [discipline || '', localisation || '', emplacementCode || '', nature || '', categorie || ''].join('|');

  React.useEffect(() => {
    let vivant = true;
    const p = new URLSearchParams({
      nom: nom || '', categorie: categorie || '', nature: nature || '',
      discipline: discipline || '', localisation: localisation || '', emplacementCode: emplacementCode || '',
    });
    void fetch(`/api/ged/ged/equip/postes-proposes?${p.toString()}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: ResultatProposition | null) => {
        if (!vivant) return;
        setRes(j);
        // La pré-coche est posée ICI, dans la continuation de la lecture — et
        // non dans un effet : `react-hooks/set-state-in-effect` est une ERREUR
        // de lint dans ce dépôt, et remonter l'état du parent depuis un effet
        // la déclencherait.
        if (j && !touche.current) {
          onChoix((j.propositions || []).filter((x) => x.confiance === 'sur').map((x) => x.code));
        }
      })
      .catch(() => { if (vivant) setRes(null); });
    return () => { vivant = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [cle]);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant || !j) return;
        const l = (Array.isArray(j) ? j : j.postes || []) as Record<string, unknown>[];
        setTousPostes(l.filter((p) => p.actif !== false)
          .map((p) => ({ code: String(p.code), libelle: String(p.libelle || p.code) })));
      })
      .catch(() => { /* la correction manuelle reste indisponible, la proposition non */ });
    return () => { vivant = false; };
  }, []);

  const basculer = (code: string) => onChoix(choix.includes(code) ? choix.filter((c) => c !== code) : [...choix, code]);

  if (!res) return null;

  // Un ACCESSOIRE ne se rattache pas : on n'affiche pas des cases à cocher qu'il
  // faudrait ensuite refuser, on explique où va le rattachement.
  if (res.redirection) {
    return (
      <div style={encadre('var(--amber-50)', 'var(--amber-300)')}>
        <strong style={{ fontSize: 12.5 }}>Poste de travail</strong>
        <p style={{ fontSize: 12.5, margin: '6px 0 0', lineHeight: 1.55 }}>{res.redirection.message}</p>
      </div>
    );
  }

  const proposes = new Set(res.propositions.map((p) => p.code));
  const autres = tousPostes.filter((p) => !proposes.has(p.code));

  return (
    <div style={encadre('var(--gray-25)', 'var(--border-default)')}>
      <strong style={{ fontSize: 12.5 }}>Poste de travail — proposition</strong>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Proposée d’après ce que l’outil sait déjà : la discipline et le lieu que vous venez de saisir,
        confrontés aux appareils <em>déjà</em> rattachés. Tout se décoche, tout se corrige, et un refus ne
        laisse aucune trace.
      </p>

      {!res.propositions.length ? (
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>{res.silence}</p>
      ) : (
        <div style={{ display: 'grid', gap: 7 }}>
          {res.propositions.map((p) => (
            <label key={p.code} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.45, cursor: 'pointer' }}>
              <input type="checkbox" checked={choix.includes(p.code)} onChange={() => basculer(p.code)}
                style={{ marginTop: 2 }} />
              <span>
                <strong>{p.libelle}</strong>
                {p.confiance === 'probable' && (
                  <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}> · à confirmer</span>
                )}
                <br />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.motif}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      {!!res.ecartes.length && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {res.ecartes.map((e) => e.libelle).join(', ')} n’{res.ecartes.length > 1 ? 'apparaissent' : 'apparaît'} pas
          dans la proposition : {res.ecartes[0].pourquoi} {/* la garde, et elle seule */}
          Rattachable à la main ci-dessous.
        </p>
      )}

      <Bouton taille="sm" type="button" onClick={() => setOuvertTous(!ouvertTous)} aria-expanded={ouvertTous} style={{ marginTop: 10 }}>
        <I n={ouvertTous ? 'chevron-bas' : 'chevron-right'} s={12} />
        {ouvertTous ? 'Masquer' : 'Choisir un autre poste'} ({autres.length})
      </Bouton>
      {ouvertTous && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 5, marginTop: 8 }}>
          {autres.map((p) => (
            <label key={p.code} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={choix.includes(p.code)} onChange={() => basculer(p.code)} />
              {p.libelle}
            </label>
          ))}
        </div>
      )}

      {!!choix.length && (
        <p style={{ fontSize: 12.5, margin: '10px 0 0', color: 'var(--green-700)', fontWeight: 600 }}>
          {choix.length} poste(s) retenu(s) — ils seront rattachés à l’enregistrement de la fiche.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 4 · Le CHIFFRAGE, et la reprise PROPOSÉE                                  */
/* ------------------------------------------------------------------------ */

/**
 * L'état en chiffres, et ce qu'une reprise POURRAIT rattacher.
 *
 * ⚠️ Aucun bouton « tout appliquer ». C'est la règle tenue le 06/08 sur les 134
 * accessoires proposés : le geste appartient au laboratoire. La reprise se relit
 * ligne à ligne et se valide poste par poste, depuis la fiche du poste.
 */
export function EtatRattachements() {
  const [chiffrage, setChiffrage] = React.useState<Chiffrage | null>(null);
  const [reprise, setReprise] = React.useState<{
    lignes: { equipement: string; nom: string; discipline?: string | null; localisation?: string | null;
      propositions: Proposition[]; silence: string | null }[];
    nbAvecProposition: number; nbSansProposition: number; nbEcartees: number;
  } | null>(null);
  const [ouvert, setOuvert] = React.useState(false);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes/equipements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j) setChiffrage(j.chiffrage); })
      .catch(() => { /* le panneau reste muet plutôt que d'afficher des zéros faux */ });
    return () => { vivant = false; };
  }, []);

  React.useEffect(() => {
    if (!ouvert) return () => { /* rien à annuler */ };
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes/equipements/reprise', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j) setReprise(j); })
      .catch(() => { /* idem */ });
    return () => { vivant = false; };
  }, [ouvert]);

  if (!chiffrage) return null;
  const c = chiffrage;

  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 4px' }}>Où en est le rattachement du parc</h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Recalculé à chaque lecture, jamais stocké — comme le centre de tâches et le registre des risques.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
        {([
          [`${c.rattaches} / ${c.mesure}`, 'équipements de mesure rattachés à au moins un poste'],
          [`${c.sansPosteATraiter}`, 'sans poste et RESTANT à traiter — la question « qui s’en sert ? » y est sans réponse'],
          [`${c.sansPosteEcartes}`, 'sans poste mais ÉCARTÉS par une décision signée — ils ne sont plus redemandés, ils ne sont pas effacés'],
          [`${c.rattachesHabilitation}`, 'rattachés par une habilitation exigée du poste'],
          [`${c.rattachesPose}`, 'rattachés à la main'],
          [`${c.postesAvecEquipement} / ${c.postes}`, 'postes portant au moins un équipement'],
          [`${c.accessoires}`, `accessoires — dont ${c.accessoiresSansAutomate} sans automate désigné, donc invisibles de tout poste`],
        ] as const).map(([n, quoi]) => (
          <div key={quoi} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '9px 11px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>{n}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>{quoi}</div>
          </div>
        ))}
      </div>
      {!!c.postesSansEquipement.length && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          Postes sans le moindre équipement : {c.postesSansEquipement.join(' · ')}. Pour un poste d’accueil
          ou de prélèvement, c’est normal — ce n’est pas présenté comme un défaut.
        </p>
      )}

      <Bouton taille="sm" type="button" onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert} style={{ marginTop: 12 }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={12} />
        Ce qu’une reprise pourrait proposer
      </Bouton>
      {ouvert && (
        <div style={{ marginTop: 10 }}>
          {!reprise ? <p style={{ fontSize: 12.5, margin: 0 }}>Lecture…</p> : (
            <>
              <p style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.55 }}>
                <strong>{reprise.nbAvecProposition}</strong> équipement(s) sans poste pour lesquels l’outil a
                quelque chose à proposer, <strong>{reprise.nbSansProposition}</strong> pour lesquels il n’a rien
                à dire, <strong>{reprise.nbEcartees}</strong> écarté(s) par décision — ces derniers ne sont
                comptés dans ni l’un ni l’autre, et ne reviennent pas dans la séance.
                {' '}<strong>Rien n’est appliqué</strong> : chaque ligne se valide depuis la fiche du poste
                concerné. Poser deux cents rattachements d’un bloc, ce serait deux cents décisions que personne
                n’aurait prises.
              </p>
              {/* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle. ⚠️ Plus de
                  `slice(0, 60)` : la liste était coupée EN SILENCE aux soixante
                  premières fiches — le tableau les montre toutes, et dit combien. */}
              <Tableau
                id="rattachements-propositions"
                ajusterLargeur
                niveau="simple"
                recherche="Code, désignation, proposition…"
                lignes={reprise.lignes.filter((l) => l.propositions.length)}
                cleLigne={(l) => l.equipement}
                vide={{ titre: 'Aucune proposition.', indice: 'L’outil n’a rien à proposer pour les équipements sans poste.' }}
                colonnes={[
                  /* Code et nom séparés, comme au tableau du poste. */
                  { cle: 'equipement', libelle: 'Code', valeur: (l) => l.equipement, style: { width: 68, fontFamily: 'var(--font-mono)', verticalAlign: 'top' }, rendu: (l) => l.equipement },
                  { cle: 'nom', libelle: 'Désignation', valeur: (l) => l.nom, rendu: (l) => l.nom },
                  {
                    cle: 'propositions', libelle: 'Postes proposés', style: { color: 'var(--text-muted)' },
                    valeur: (l) => l.propositions.map((p) => p.libelle).join(' · '),
                    rendu: (l) => l.propositions.map((p) => `${p.libelle}${p.confiance === 'probable' ? ' (à confirmer)' : ''}`).join(' · '),
                  },
                ]}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 5 · La SÉANCE de reprise — décider les fiches restantes en une fois       */
/* ------------------------------------------------------------------------ */

export type LigneReprise = {
  equipement: string; nom: string;
  discipline?: string | null; localisation?: string | null;
  propositions: Proposition[]; silence: string | null;
};

/** Ce que dit une confiance, en toutes lettres. Le chiffre est celui de la
 *  validation croisée du 07/08 — il est écrit à l'écran parce que c'est lui,
 *  et non un mot, qui permet de décider s'il faut vérifier. */
const DIT_CONFIANCE: Record<'sur' | 'probable', { court: string; long: string; fond: string; bord: string; texte: string }> = {
  sur: {
    court: 'la discipline le porte',
    long: 'La DISCIPLINE de la fiche est celle des appareils déjà rattachés à ce poste — 86 % de justesse '
      + 'à la validation croisée du 07/08. C’est ce qui a permis de poser les 55 premiers rattachements.',
    fond: 'var(--blue-50)', bord: 'var(--blue-300)', texte: 'var(--blue-700)',
  },
  probable: {
    court: 'seul le LIEU le porte — 46 % de justesse',
    long: 'Aucune discipline ne relie cette fiche à ce poste : la proposition ne repose que sur le LIEU, '
      + 'mesuré à 46 % de justesse le 07/08 — une salle abrite plusieurs paillasses. Elle n’est donc PAS '
      + 'cochée d’avance : c’est une piste à vérifier, pas un rattachement.',
    fond: 'var(--amber-50)', bord: 'var(--amber-300)', texte: 'var(--amber-700)',
  },
};

/**
 * Les fiches qui restent, décidées EN UNE SÉANCE.
 *
 * ── Pourquoi cet écran existe ──────────────────────────────────────────────
 *
 * La reprise du 07/08 a posé les 55 rattachements que la DISCIPLINE portait, et
 * s’est arrêtée devant ceux que seul le LIEU proposait : 46 % de justesse, une
 * erreur sur deux. Ces fiches-là demandent un jugement humain, et le panneau
 * qui les listait ne savait que les AFFICHER — il fallait ensuite ouvrir chaque
 * poste et refaire le rapprochement de tête. Vingt-et-une navigations pour
 * vingt-et-une décisions de dix secondes : personne ne le fait deux fois.
 *
 * Ici, chaque fiche montre ce que l’outil SAIT (désignation, discipline,
 * emplacement), ce qu’il propose, et POURQUOI il le propose. On accepte, on
 * corrige, et on enregistre d’un coup.
 *
 * ⚠️ Rien n’est coché d’avance quand la proposition ne tient qu’au lieu. C’est
 * la règle qui a présidé aux 55 déjà posés, et la seule qui rende la case
 * lisible : une case cochée d’avance est une case que personne ne relit.
 *
 * ⚠️ Les liens POSÉS et les liens DÉDUITS d’une habilitation ne sont jamais
 * fondus. L’enregistrement renvoie la liste des seuls posés du poste, augmentée
 * de ce qui vient d’être décidé — recopier le déduit le figerait, et retirer
 * l’habilitation ne retirerait plus l’équipement.
 *
 * ⚠️ ÉCARTER est PERSISTANT (08/08/2026). La liste reste DÉRIVÉE des fiches sans
 * poste (`repriseProposee`), mais la DÉCISION, elle, s’écrit : `posteEcarte` /
 * `posteEcarteMotif` / `posteEcartePar` / `posteEcarteLe` sur `ged-equipement`,
 * exactement comme `ncEcartee` sur un passage de contrôle interne. Un bouton qui
 * aurait oublié au rechargement aurait fait redemander éternellement la même
 * chose — jusqu’à ce que plus personne n’ouvre la séance.
 *
 * ⚠️ Le MOTIF est obligatoire (10 caractères), et les écartées sont montrées à
 * PART, jamais fondues dans la liste à traiter : une fiche écartée n’est pas une
 * fiche « sans poste » de plus, c’est une question close. Le compteur de ce qui
 * reste ne la compte pas, et le panneau « écartées » permet de la REPRENDRE —
 * sans quoi un clic malheureux la sortirait définitivement, et l’usage réel
 * deviendrait « ne jamais écarter ».
 */
export function SeanceRattachements() {
  /* ⚠️ Lu par SÉLECTEUR, et non par le magasin entier. Cet écran ne tient AUCUNE de
     ses données du magasin — la vue, les lignes et les postes viennent de son
     propre `fetch`, et ses états vides sont déjà gardés par `null` (cf.
     l'exemption motivée dans `qualite-faux-vide`). Le paramétrage ne lui sert
     qu'à NOMMER la personne qui a écarté une fiche. */
  const settings = useGed((s) => s.settings);
  const [lignes, setLignes] = React.useState<LigneReprise[] | null>(null);
  const [postes, setPostes] = React.useState<VuePoste[] | null>(null);
  const [ecartees, setEcartees] = React.useState<LigneEcartee[]>([]);
  const [choix, setChoix] = React.useState<Record<string, string[]>>({});
  // Le motif d'écartement, par fiche. Il n'est JAMAIS pré-rempli : un motif
  // suggéré est un motif que personne n'écrit, donc une trace qui ne dit rien.
  const [motifs, setMotifs] = React.useState<Record<string, string>>({});
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [refus, setRefus] = React.useState<string[]>([]);
  const [enCours, setEnCours] = React.useState(false);
  const [ouvert, setOuvert] = React.useState(false);
  // Compteur de rechargement — jamais un setState au corps d'un effet.
  const [tour, setTour] = React.useState(0);

  React.useEffect(() => {
    if (!ouvert) return () => { /* rien à annuler */ };
    let vivant = true;
    void Promise.all([
      // `?ecartees=1` : sans lui, une fiche écartée serait invisible partout et
      // `poste-reprendre` existerait sans qu'aucun écran puisse la désigner.
      fetch('/api/ged/ged/rh/postes/equipements/reprise?ecartees=1', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)),
      fetch('/api/ged/ged/rh/postes/equipements', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([rep, reg]) => {
        if (!vivant) return;
        if (!rep || !reg) { setErreur('La reprise n’a pas pu être lue. Rien n’a été modifié.'); return; }
        setErreur('');
        setLignes((rep.lignes || []).filter((l: LigneReprise) => l.propositions.length));
        setEcartees((rep.ecartees || []) as LigneEcartee[]);
        setPostes(reg.postes || []);
      })
      .catch(() => { if (vivant) setErreur('La reprise n’a pas pu être lue. Rien n’a été modifié.'); });
    return () => { vivant = false; };
  }, [ouvert, tour]);

  /** Écarter ou reprendre — un seul chemin, parce que c'est le même acte lu
   *  dans les deux sens : une décision signée, avec son motif, qui se journalise
   *  et qui se défait. */
  const decider = async (code: string, quoi: 'poste-ecarter' | 'poste-reprendre') => {
    const motif = (motifs[code] ?? '').trim();
    if (motif.length < 10) return;
    setEnCours(true); setMessage(''); setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/equip/${encodeURIComponent(code)}/${quoi}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) {
      setErreur(`${code} — ${d?.error?.message || 'refusé par le moteur. Rien n’a été écrit.'}`);
      return;
    }
    setMotifs((m) => ({ ...m, [code]: '' }));
    setMessage(quoi === 'poste-ecarter'
      ? `${code} écarté de la reprise, motif enregistré et journalisé. Il ne sera plus redemandé — et il reste `
        + 'listé, avec sa raison, dans « Fiches écartées » : la décision se relit et se défait.'
      : `${code} remis dans la reprise. L’écartement reste au journal ; seule sa conséquence est levée.`);
    setTour((t) => t + 1);
  };

  // Ce qui est retenu pour une fiche : le brouillon s'il existe, sinon le
  // défaut — les seules propositions SÛRES. Dérivé, jamais posé dans un effet.
  const retenu = (l: LigneReprise) => choix[l.equipement]
    ?? l.propositions.filter((p) => p.confiance === 'sur').map((p) => p.code);

  const basculer = (l: LigneReprise, codePoste: string) => {
    setMessage('');
    const actuel = retenu(l);
    setChoix({
      ...choix,
      [l.equipement]: actuel.includes(codePoste)
        ? actuel.filter((c) => c !== codePoste)
        : [...actuel, codePoste],
    });
  };

  const enregistrer = async () => {
    if (!lignes || !postes) return;
    setEnCours(true);
    setMessage('');
    setErreur('');

    // Regroupé PAR POSTE : la route d'écriture porte la liste complète des
    // équipements posés d'un poste, et non un rattachement isolé.
    const parPoste = new Map<string, { equipement: string; propose: boolean }[]>();
    for (const l of lignes) {
      for (const codePoste of retenu(l)) {
        if (!parPoste.has(codePoste)) parPoste.set(codePoste, []);
        parPoste.get(codePoste)!.push({
          equipement: l.equipement,
          propose: l.propositions.some((p) => p.code === codePoste),
        });
      }
    }

    const refuses: string[] = [];
    let ecrits = 0;
    for (const [codePoste, ajouts] of parPoste) {
      const vue = postes.find((p) => p.code === codePoste);
      if (!vue) { refuses.push(`${codePoste} — poste introuvable, rien n’a été écrit dessus.`); continue; }
      // ⚠️ L'écriture REMPLACE la liste posée. Un poste qui porte des codes
      // introuvables ou des accessoires posés par erreur les perdrait en
      // silence : on ne touche pas à ce poste et on dit pourquoi.
      if (vue.introuvables.length || vue.accessoiresPoses.length) {
        refuses.push(`${vue.libelle} (${codePoste}) — non modifié : ce poste porte des rattachements à corriger `
          + `d’abord (${[...vue.introuvables, ...vue.accessoiresPoses].join(', ')}). `
          + 'Les réécrire d’ici les effacerait sans que personne ne le voie.');
        continue;
      }
      const corps = [
        ...vue.equipements.filter((e) => e.origine === 'pose').map((e) => ({ code: e.code })),
        ...ajouts.map((a) => (a.propose
          ? { code: a.equipement, origine: 'proposition' }
          : { code: a.equipement })),
      ];
      const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(codePoste)}/equipements`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ equipements: corps, base: vue.equipements.filter((e) => e.origine === 'pose').map((e) => e.code) }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { refuses.push(`${vue.libelle} (${codePoste}) — ${d?.error?.message || 'refusé par le moteur.'}`); continue; }
      ecrits += (d?.ajoutes || []).length;
    }

    setEnCours(false);
    setRefus(refuses);
    setChoix({});
    setMessage(ecrits
      ? `${ecrits} rattachement(s) posé(s) sur ${parPoste.size - refuses.length} poste(s). `
        + 'Les fiches traitées quittent la liste : elles portent désormais un poste.'
      : 'Aucun rattachement n’a été posé — aucune case n’était cochée, ou tout a été refusé.');
    setTour((t) => t + 1);
  };

  const total = lignes
    ? lignes.reduce((n, l) => n + retenu(l).length, 0)
    : 0;

  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 4px' }}>Décider les fiches qui restent — en une séance</h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
        La reprise du 07/08 a posé ce que la <strong>discipline</strong> portait. Ce qui reste n’est proposé
        que par le <strong>lieu</strong> — 46 % de justesse, une erreur sur deux : rien n’est coché d’avance,
        et chaque proposition dit sur quoi elle repose. On accepte, on corrige, on enregistre d’un coup.
      </p>

      <Bouton taille="sm" type="button" onClick={() => { setOuvert(!ouvert); setMessage(''); }} aria-expanded={ouvert}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={12} />
        {ouvert ? 'Refermer la séance' : 'Ouvrir la séance de rattachement'}
      </Bouton>

      {ouvert && (
        <div style={{ marginTop: 12 }}>
          {!!erreur && (
            <div style={{ ...encadre('var(--red-50)', 'var(--red-300)'), marginBottom: 12 }}>
              <strong style={{ fontSize: 12.5 }}>{erreur}</strong>
            </div>
          )}
          {!!message && (
            <div style={{ ...encadre('var(--green-50)', 'var(--green-700)'), marginBottom: 12 }}>
              <strong style={{ fontSize: 12.5, color: 'var(--green-700)' }}>{message}</strong>
            </div>
          )}
          {!!refus.length && (
            <div style={{ ...encadre('var(--amber-50)', 'var(--amber-300)'), marginBottom: 12 }}>
              <strong style={{ fontSize: 12.5 }}>Postes non modifiés</strong>
              <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>
                {refus.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
          )}

          {!lignes || !postes ? <p style={{ fontSize: 12.5, margin: 0 }}>Lecture…</p> : !lignes.length ? (
            <EmptyState titre="Plus aucune fiche en attente"
              indice="Toutes les fiches sans poste pour lesquelles l’outil avait quelque chose à proposer ont été décidées. Les fiches sur lesquelles il se tait ne sont pas listées ici : il n’a rien à en dire, et l’inventer serait pire." />
          ) : (
            <>
              <p style={{ fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.55 }}>
                <strong>{lignes.length}</strong> fiche(s) en attente d’une décision ·
                {' '}<strong>{total}</strong> rattachement(s) actuellement retenu(s).
              </p>

              <div style={{ display: 'grid', gap: 10 }}>
                {lignes.map((l) => {
                  const pris = retenu(l);
                  const proposes = new Set(l.propositions.map((p) => p.code));
                  const autres = (postes || []).filter((p) => !proposes.has(p.code));
                  return (
                    <div key={l.equipement} style={{
                      border: `1px solid ${pris.length ? 'var(--cyan-200)' : 'var(--border-subtle)'}`,
                      borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                      background: pris.length ? 'var(--cyan-50)' : 'transparent',
                    }}>
                      {/* CE QUE L'OUTIL SAIT de la fiche — sans quoi la proposition
                          se juge à l'aveugle : la discipline vide EST le motif du
                          repli sur le lieu, et il faut la voir pour le comprendre. */}
                      <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                        <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{l.equipement}</strong> — {l.nom}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 6 }}>
                        discipline : {l.discipline || <em>non renseignée</em>}
                        {' · '}emplacement : {l.localisation || <em>non renseigné</em>}
                      </div>

                      <div style={{ display: 'grid', gap: 5 }}>
                        {l.propositions.map((p) => {
                          const dit = DIT_CONFIANCE[p.confiance];
                          return (
                            <label key={p.code} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
                              <input type="checkbox" checked={pris.includes(p.code)}
                                onChange={() => basculer(l, p.code)}
                                style={{ marginTop: 3, flex: '0 0 auto' }} />
                              <span style={{ lineHeight: 1.5 }}>
                                <strong>{p.libelle}</strong>
                                {' '}
                                <span title={dit.long} style={{
                                  display: 'inline-block', fontSize: 12.5, fontWeight: 700, padding: '1px 7px',
                                  borderRadius: 'var(--radius-pill)', background: dit.fond, color: dit.texte,
                                  border: `1px solid ${dit.bord}`, whiteSpace: 'nowrap',
                                }}>{dit.court}</span>
                                <br />
                                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.motif}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      {/* Les postes retenus HORS proposition — la correction. */}
                      {pris.filter((c) => !proposes.has(c)).map((c) => (
                        <div key={c} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, marginTop: 5 }}>
                          <strong>{(postes || []).find((p) => p.code === c)?.libelle || c}</strong>
                          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>choisi à la main — aucune proposition ne le portait</span>
                          <Bouton taille="sm" onClick={() => basculer(l, c)}>
                            <I n="x" s={11} /> Retirer
                          </Bouton>
                        </div>
                      ))}

                      <label style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'block', marginTop: 6 }}>
                        Corriger — rattacher à un autre poste
                        <select style={{ ...inputStyle, height: 32, fontSize: 12.5 }} value=""
                          onChange={(e) => { if (e.target.value) basculer(l, e.target.value); }}>
                          <option value="">— aucun autre poste —</option>
                          {autres.map((p) => (
                            <option key={p.code} value={p.code} disabled={pris.includes(p.code)}>
                              {p.libelle} ({p.code})
                            </option>
                          ))}
                        </select>
                      </label>

                      {/* ÉCARTER — la troisième réponse, à côté d'« accepter » et
                          de « corriger ». Sans elle, une fiche qui n'a RIEN à
                          faire sur un poste (appareil au rebut, prêt en cours,
                          réserve) revient à chaque ouverture et finit par faire
                          fermer l'écran. Le motif est obligatoire : c'est la
                          seule trace que ce refus laissera, et c'est elle que
                          l'auditeur ouvrira. */}
                      <div style={{
                        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                        marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-subtle)',
                      }}>
                        <input
                          type="text"
                          value={motifs[l.equipement] ?? ''}
                          onChange={(e) => setMotifs({ ...motifs, [l.equipement]: e.target.value })}
                          placeholder="Motif si vous écartez cette fiche (10 caractères minimum)"
                          aria-label={`Motif d’écartement de ${l.equipement}`}
                          style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1, minWidth: 260 }}
                        />
                        <Bouton taille="sm" type="button" disabled={enCours || (motifs[l.equipement] ?? '').trim().length < 10} onClick={() => void decider(l.equipement, 'poste-ecarter')}>
                          <I n="x" s={11} /> Écarter
                        </Bouton>
                      </div>
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.45 }}>
                        Écarter ne rattache rien et ne supprime rien : la fiche cesse d’être proposée,
                        reste comptée « sans poste », et se retrouve — avec son motif — dans « Fiches écartées ».
                      </p>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <Bouton variante="primaire" disabled={enCours || !total} onClick={() => void enregistrer()}>
                  <I n="check" s={14} /> Enregistrer les {total} rattachement(s)
                </Bouton>
                {!!Object.keys(choix).length && (
                  <Bouton taille="sm" onClick={() => { setChoix({}); setMessage(''); }}>
                    Revenir aux propositions
                  </Bouton>
                )}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: 560 }}>
                  Une fiche laissée sans case cochée n’est pas écrite et <strong>reviendra</strong> à la
                  prochaine ouverture — c’est voulu : « je ne sais pas encore » n’est pas une décision.
                  Pour qu’elle ne revienne plus, il faut l’<strong>écarter</strong> avec un motif.
                </span>
              </div>
            </>
          )}

          {/* ══ Les ÉCARTÉES — un tableau SÉPARÉ ═════════════════════════════
              Ni fondues dans la liste à traiter (elles y seraient redemandées
              sans fin), ni supprimées (la décision serait irréversible et
              invisible). Même patron, à la lettre, que les propositions de NC
              écartées du contrôle interne. */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 13, margin: '0 0 4px' }}>
              Fiches écartées ({ecartees.length})
            </h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
              Elles ne sont plus proposées, mais elles ne sont pas effacées : elles restent des équipements
              qu’aucun poste n’utilise, et le chiffrage les compte à ce titre. Chacune porte son motif et son
              auteur ; on peut la remettre dans la séance, avec un motif à son tour.
            </p>
            {!ecartees.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
                Aucune fiche écartée.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {ecartees.map((e) => (
                  <div key={e.equipement} style={{
                    border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
                    padding: '9px 11px', background: 'transparent',
                  }}>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{e.equipement}</strong> — {e.nom}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      discipline : {e.discipline || <em>non renseignée</em>}
                      {' · '}emplacement : {e.localisation || <em>non renseigné</em>}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 4 }}>
                      Écarté{e.ecartement.par ? ` par ${userName(settings, e.ecartement.par)}` : ''}
                      {e.ecartement.le ? ` le ${fmtDate(e.ecartement.le)}` : ''} — {e.ecartement.motif}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                      <input
                        type="text"
                        value={motifs[e.equipement] ?? ''}
                        onChange={(ev) => setMotifs({ ...motifs, [e.equipement]: ev.target.value })}
                        placeholder="Motif de la reprise (10 caractères minimum)"
                        aria-label={`Motif de reprise de ${e.equipement}`}
                        style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1, minWidth: 260 }}
                      />
                      <Bouton taille="sm" type="button" disabled={enCours || (motifs[e.equipement] ?? '').trim().length < 10} onClick={() => void decider(e.equipement, 'poste-reprendre')}>
                        Remettre dans la séance
                      </Bouton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
